import React from "react";

const DRAG_THRESHOLD = 6;

function buildArcPath(cx, cy, outerR, innerRatio, startDeg, endDeg) {
    const innerR = outerR * Math.max(0.01, Math.min(0.99, innerRatio));
    const toRad = (d) => (d * Math.PI) / 180;
    const s = toRad(startDeg); const e = toRad(endDeg);
    let sweep = endDeg - startDeg;
    if (sweep <= 0) sweep += 360;
    if (sweep >= 360) {
        return [
            `M ${cx + outerR} ${cy}`, `A ${outerR} ${outerR} 0 1 1 ${cx - outerR} ${cy}`,
            `A ${outerR} ${outerR} 0 1 1 ${cx + outerR} ${cy}`,
            `M ${cx + innerR} ${cy}`, `A ${innerR} ${innerR} 0 1 0 ${cx - innerR} ${cy}`,
            `A ${innerR} ${innerR} 0 1 0 ${cx + innerR} ${cy}`,
        ].join(" ");
    }
    const large = sweep > 180 ? 1 : 0;
    const x1 = cx + outerR * Math.cos(s); const y1 = cy + outerR * Math.sin(s);
    const x2 = cx + outerR * Math.cos(e); const y2 = cy + outerR * Math.sin(e);
    const x3 = cx + innerR * Math.cos(e); const y3 = cy + innerR * Math.sin(e);
    const x4 = cx + innerR * Math.cos(s); const y4 = cy + innerR * Math.sin(s);
    return `M ${x1} ${y1} A ${outerR} ${outerR} 0 ${large} 1 ${x2} ${y2} L ${x3} ${y3} A ${innerR} ${innerR} 0 ${large} 0 ${x4} ${y4} Z`;
}

function buildPolyPath(pts, curves) {
    if (!pts || pts.length < 3) return "";
    const n = pts.length;
    let d = `M ${pts[0].x} ${pts[0].y}`;
    for (let i = 0; i < n; i++) {
        const ni = (i + 1) % n;
        const ctrl = curves?.[`${i}-${ni}`];
        const to = pts[ni];
        d += ctrl ? ` Q ${ctrl.cx} ${ctrl.cy} ${to.x} ${to.y}` : ` L ${to.x} ${to.y}`;
    }
    return d + ' Z';
}


function BlockShape({ b, fill, stroke, strokeWidth }) {
    const shape = b.shape || "rect";
    const sp = b.shapeParams || {};
    const w = b.w; const h = b.h;

    if (shape === "ellipse") {
        return <ellipse cx={b.x + w / 2} cy={b.y + h / 2} rx={w / 2} ry={h / 2} fill={fill} stroke={stroke} strokeWidth={strokeWidth} />;
    }
    if (shape === "arc") {
        const outerR = Math.min(w, h) / 2;
        const d = buildArcPath(b.x + w / 2, b.y + h / 2, outerR, sp.innerRatio ?? 0.5, sp.startAngle ?? 180, sp.endAngle ?? 360);
        return <path d={d} fill={fill} stroke={stroke} strokeWidth={strokeWidth} />;
    }
    if (shape === "trapezoid") {
        const topRatio = sp.topRatio ?? 0.7;
        const topW = w * topRatio;
        const ox = b.x + (w - topW) / 2;
        const path = `M ${ox} ${b.y} L ${ox + topW} ${b.y} L ${b.x + w} ${b.y + h} L ${b.x} ${b.y + h} Z`;
        return <path d={path} fill={fill} stroke={stroke} strokeWidth={strokeWidth} />;
    }
    if (shape === "triangle") {
        const path = `M ${b.x + w / 2} ${b.y} L ${b.x + w} ${b.y + h} L ${b.x} ${b.y + h} Z`;
        return <path d={path} fill={fill} stroke={stroke} strokeWidth={strokeWidth} />;
    }
    if (shape === "polygon") {
        const pts = sp.points ?? [];
        if (pts.length < 3) return null;
        const d = buildPolyPath(pts, sp.curves ?? {});
        return <path d={d} fill={fill} stroke={stroke} strokeWidth={strokeWidth} />;
    }
    return <rect x={b.x} y={b.y} width={w} height={h} rx="18" ry="18" fill={fill} stroke={stroke} strokeWidth={strokeWidth} />;
}

export default function SeatMapViewport({
    layout,
    activePackageId,
    selectedSeatIds,
    maxSelectable = 9,
    onToggleSeat,
    selectedCount = 0,
    soldSeatIds = [],
    heldSeatIds = [],
}) {
    const [scale, setScale] = React.useState(1);
    const [offset, setOffset] = React.useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = React.useState(false);

    const soldSet = React.useMemo(() => new Set(soldSeatIds.map(String)), [soldSeatIds]);
    const heldSet = React.useMemo(() => new Set(heldSeatIds.map(String)), [heldSeatIds]);

    const containerRef = React.useRef(null);
    const scaleRef = React.useRef(1);
    const offsetRef = React.useRef({ x: 0, y: 0 });
    const mouseDragRef = React.useRef({ active: false, startX: 0, startY: 0, lastX: 0, lastY: 0, moved: false });
    const touchDragRef = React.useRef({ startX: 0, startY: 0, lastX: 0, lastY: 0, moved: false });
    const pinchRef = React.useRef({ active: false, startDist: 1, startScale: 1, lastCenter: null });
    const lastTapRef = React.useRef(0);

    const stage = layout?.stage ?? null;
    const blocks = Array.isArray(layout?.blocks) ? layout.blocks : [];

    const activeBlock = React.useMemo(
        () => !activePackageId ? null : (blocks.find((b) => String(b.packageId) === String(activePackageId)) ?? null),
        [blocks, activePackageId]
    );

    const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

    const hexToRgba = React.useCallback((hex, a = 1) => {
        if (!hex || typeof hex !== "string") return `rgba(255,255,255,${a})`;
        let h = hex.trim().replace("#", "");
        if (h.length === 3) h = h.split("").map((c) => c + c).join("");
        const n = parseInt(h, 16);
        if (Number.isNaN(n)) return `rgba(255,255,255,${a})`;
        return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
    }, []);

    const commitTransform = (nextScale, nextOffset) => {
        scaleRef.current = nextScale;
        offsetRef.current = nextOffset;
        setScale(nextScale);
        setOffset(nextOffset);
    };

    const applyZoom = React.useCallback((nextScale, pivotX, pivotY) => {
        const clamped = clamp(nextScale, 0.3, 6);
        const ratio = clamped / scaleRef.current;
        commitTransform(clamped, {
            x: pivotX - (pivotX - offsetRef.current.x) * ratio,
            y: pivotY - (pivotY - offsetRef.current.y) * ratio,
        });
    }, []);

    const zoomBlock = React.useCallback(() => {
        const el = containerRef.current;
        if (!el || !activeBlock) return;
        const rect = el.getBoundingClientRect();
        if (!rect.width || !rect.height) return;
        const pad = 48;
        const r = 8;
        let minX = activeBlock.x;
        let minY = activeBlock.y;
        let maxX = activeBlock.x + activeBlock.w;
        let maxY = activeBlock.y + activeBlock.h;
        if (Array.isArray(activeBlock.seats)) {
            activeBlock.seats.forEach((s) => {
                const sx = activeBlock.x + (s.x || 0);
                const sy = activeBlock.y + (s.y || 0);
                if (sx - r < minX) minX = sx - r;
                if (sy - r < minY) minY = sy - r;
                if (sx + r > maxX) maxX = sx + r;
                if (sy + r > maxY) maxY = sy + r;
            });
        }
        const bw = Math.max(1, maxX - minX);
        const bh = Math.max(1, maxY - minY);
        const cx = (minX + maxX) / 2;
        const cy = (minY + maxY) / 2;
        const fit = Math.min((rect.width - pad * 2) / bw, (rect.height - pad * 2) / bh);
        const nextScale = clamp(fit, 0.3, 6);
        commitTransform(nextScale, {
            x: rect.width / 2 - cx * nextScale,
            y: rect.height / 2 - cy * nextScale,
        });
    }, [activeBlock]);

    React.useEffect(() => {
        zoomBlock();
    }, [zoomBlock]);

    const zoomAtCenter = (factor) => {
        const el = containerRef.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        applyZoom(scaleRef.current * factor, r.width / 2, r.height / 2);
    };

    React.useEffect(() => {
        const el = containerRef.current;
        if (!el) return;

        const onWheel = (e) => {
            e.preventDefault();
            const r = el.getBoundingClientRect();
            applyZoom(
                scaleRef.current * (e.deltaY < 0 ? 1.12 : 1 / 1.12),
                e.clientX - r.left,
                e.clientY - r.top
            );
        };

        const onTouchStart = (e) => {
            if (e.touches.length === 1) {
                const t = e.touches[0];
                const now = Date.now();
                if (now - lastTapRef.current < 300) {
                    lastTapRef.current = 0;
                    zoomBlock();
                    return;
                }
                lastTapRef.current = now;
                touchDragRef.current = {
                    startX: t.clientX, startY: t.clientY,
                    lastX: t.clientX, lastY: t.clientY,
                    moved: false,
                };
                pinchRef.current.active = false;
            } else if (e.touches.length === 2) {
                const [t1, t2] = [e.touches[0], e.touches[1]];
                const d = Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
                pinchRef.current = {
                    active: true,
                    startDist: Math.max(d, 1),
                    startScale: scaleRef.current,
                    lastCenter: { x: (t1.clientX + t2.clientX) / 2, y: (t1.clientY + t2.clientY) / 2 },
                };
            }
        };

        const onTouchMove = (e) => {
            e.preventDefault();
            if (e.touches.length === 1 && !pinchRef.current.active) {
                const t = e.touches[0];
                const td = touchDragRef.current;
                const dx = t.clientX - td.lastX;
                const dy = t.clientY - td.lastY;
                td.lastX = t.clientX;
                td.lastY = t.clientY;
                if (Math.hypot(t.clientX - td.startX, t.clientY - td.startY) > DRAG_THRESHOLD) td.moved = true;
                const newOffset = { x: offsetRef.current.x + dx, y: offsetRef.current.y + dy };
                offsetRef.current = newOffset;
                setOffset(newOffset);
            } else if (e.touches.length === 2) {
                const [t1, t2] = [e.touches[0], e.touches[1]];
                const d = Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
                const cx = (t1.clientX + t2.clientX) / 2;
                const cy = (t1.clientY + t2.clientY) / 2;
                const r = el.getBoundingClientRect();
                const pivotX = cx - r.left;
                const pivotY = cy - r.top;
                const nextScale = clamp(pinchRef.current.startScale * (d / pinchRef.current.startDist), 0.3, 6);
                const ratio = nextScale / scaleRef.current;
                const lastC = pinchRef.current.lastCenter;
                const newOffset = {
                    x: pivotX - (pivotX - (offsetRef.current.x + (cx - lastC.x))) * ratio,
                    y: pivotY - (pivotY - (offsetRef.current.y + (cy - lastC.y))) * ratio,
                };
                pinchRef.current.lastCenter = { x: cx, y: cy };
                scaleRef.current = nextScale;
                offsetRef.current = newOffset;
                setScale(nextScale);
                setOffset(newOffset);
            }
        };

        const onTouchEnd = (e) => {
            if (e.touches.length < 2) pinchRef.current.active = false;
            if (e.touches.length === 1) {
                const t = e.touches[0];
                touchDragRef.current.lastX = t.clientX;
                touchDragRef.current.lastY = t.clientY;
            }
        };

        el.addEventListener("wheel", onWheel, { passive: false });
        el.addEventListener("touchstart", onTouchStart, { passive: true });
        el.addEventListener("touchmove", onTouchMove, { passive: false });
        el.addEventListener("touchend", onTouchEnd, { passive: true });

        return () => {
            el.removeEventListener("wheel", onWheel);
            el.removeEventListener("touchstart", onTouchStart);
            el.removeEventListener("touchmove", onTouchMove);
            el.removeEventListener("touchend", onTouchEnd);
        };
    }, [zoomBlock, applyZoom]);

    React.useEffect(() => {
        const onMove = (e) => {
            const d = mouseDragRef.current;
            if (!d.active) return;
            const dx = e.clientX - d.lastX;
            const dy = e.clientY - d.lastY;
            d.lastX = e.clientX;
            d.lastY = e.clientY;
            if (Math.hypot(e.clientX - d.startX, e.clientY - d.startY) > DRAG_THRESHOLD) d.moved = true;
            const newOffset = { x: offsetRef.current.x + dx, y: offsetRef.current.y + dy };
            offsetRef.current = newOffset;
            setOffset(newOffset);
        };
        const onUp = () => {
            if (!mouseDragRef.current.active) return;
            mouseDragRef.current.active = false;
            setIsDragging(false);
        };
        window.addEventListener("mousemove", onMove);
        window.addEventListener("mouseup", onUp);
        return () => {
            window.removeEventListener("mousemove", onMove);
            window.removeEventListener("mouseup", onUp);
        };
    }, []);

    const onMouseDown = (e) => {
        if (e.button !== 0) return;
        e.preventDefault();
        mouseDragRef.current = {
            active: true,
            startX: e.clientX, startY: e.clientY,
            lastX: e.clientX, lastY: e.clientY,
            moved: false,
        };
        setIsDragging(true);
    };

    const handleSeatClick = (e, seatId, canPick, isDisabled, isUnavailable) => {
        e.stopPropagation();
        if (mouseDragRef.current.moved || touchDragRef.current.moved) return;
        if (!canPick || isDisabled || isUnavailable) return;
        onToggleSeat?.(seatId);
    };

    return (
        <div className="w-full">
            <div className="flex items-center justify-center gap-2 mb-3">
                <button
                    onClick={() => zoomAtCenter(1 / 1.2)}
                    className="h-10 w-10 flex items-center justify-center rounded bg-white/10 hover:bg-white/15 text-white text-xl font-bold"
                    title="Alejar"
                >
                    −
                </button>
                <button
                    onClick={zoomBlock}
                    className="h-10 px-4 rounded bg-white/10 hover:bg-white/15 text-white text-sm"
                >
                    Reiniciar vista
                </button>
                <button
                    onClick={() => zoomAtCenter(1.2)}
                    className="h-10 w-10 flex items-center justify-center rounded bg-white/10 hover:bg-white/15 text-white text-xl font-bold"
                    title="Acercar"
                >
                    +
                </button>
            </div>

            <div
                ref={containerRef}
                className="relative w-full h-[42vh] md:h-[60vh] rounded-xl overflow-hidden border border-white/10 bg-black/20 select-none"
                onMouseDown={onMouseDown}
                style={{
                    touchAction: "none",
                    cursor: isDragging ? "grabbing" : "grab",
                    userSelect: "none",
                    WebkitUserSelect: "none",
                }}
            >
                <div
                    className="absolute inset-0 opacity-30 pointer-events-none"
                    style={{
                        backgroundImage:
                            "linear-gradient(to right, rgba(255,255,255,0.06) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.06) 1px, transparent 1px)",
                        backgroundSize: "28px 28px",
                    }}
                />

                <svg
                    className="absolute inset-0 w-full h-full"
                    style={{ overflow: "visible" }}
                >
                    <g transform={`translate(${offset.x} ${offset.y}) scale(${scale})`}>
                        {stage && (
                            <g>
                                <rect
                                    x={stage.x} y={stage.y} width={stage.w} height={stage.h}
                                    rx="18" ry="18"
                                    fill="rgba(255,255,255,0.10)" stroke="rgba(255,255,255,0.25)" strokeWidth={1.5}
                                />
                                <text
                                    x={stage.x + stage.w / 2} y={stage.y + stage.h / 2}
                                    textAnchor="middle" dy="0.35em" fontSize="16"
                                    fill="rgba(255,255,255,0.9)" fontFamily="ui-sans-serif, system-ui"
                                >
                                    Escenario
                                </text>
                            </g>
                        )}

                        {blocks.map((b) => {
                            const isActive = activePackageId && String(b.packageId) === String(activePackageId);
                            return (
                                <g key={b.id}>
                                    <BlockShape
                                        b={b}
                                        fill={isActive ? hexToRgba(b.color, 0.22) : hexToRgba(b.color, 0.12)}
                                        stroke={isActive ? hexToRgba(b.color, 0.95) : "rgba(255,255,255,0.25)"}
                                        strokeWidth={isActive ? 2.2 : 1.2}
                                    />
                                    {(() => {
                                        const name = (b.name || "").toUpperCase();
                                        if (!name) return null;
                                        const fs = 11, px = 11, py = 4.5;
                                        const rw = Math.max(44, name.length * fs * 0.62 + px * 2);
                                        const rh = fs + py * 2;

                                        const isPolygon = b.shape === "polygon";
                                        const pts = isPolygon ? (b.shapeParams?.points ?? []) : [];
                                        let cx, cy;
                                        if (isPolygon && pts.length >= 3) {
                                            cx = pts.reduce((s, p) => s + p.x, 0) / pts.length;
                                            cy = pts.reduce((s, p) => s + p.y, 0) / pts.length;
                                        } else {
                                            cx = b.x + b.w / 2;
                                            cy = b.y + b.h / 2;
                                        }

                                        const bgFill = hexToRgba(b.color || "#555555", 0.92);
                                        const bgStroke = hexToRgba(b.color || "#555555", 0.55);
                                        return (
                                            <g style={{ pointerEvents: "none" }}>
                                                <rect
                                                    x={cx - rw / 2 - 1.5} y={cy - rh / 2 - 1.5}
                                                    width={rw + 3} height={rh + 3} rx={rh / 2 + 2}
                                                    fill="none" stroke={bgStroke}
                                                    strokeWidth={1.5} opacity={0.5}
                                                />
                                                <rect
                                                    x={cx - rw / 2} y={cy - rh / 2}
                                                    width={rw} height={rh} rx={rh / 2}
                                                    fill={bgFill}
                                                    stroke="rgba(255,255,255,0.25)" strokeWidth={0.8}
                                                />
                                                <text
                                                    x={cx} y={cy}
                                                    textAnchor="middle" dy="0.35em"
                                                    fontSize={fs} fontWeight="700"
                                                    fill="rgba(255,255,255,1)"
                                                    fontFamily="ui-sans-serif, system-ui"
                                                    letterSpacing="0.07em"
                                                >
                                                    {name}
                                                </text>
                                            </g>
                                        );
                                    })()}

                                    {Array.isArray(b.seats) && b.seats.map((s) => {
                                        const seatId = s.id;
                                        const isSelected = selectedSeatIds?.has?.(seatId);
                                        const canPick = isActive;
                                        const isSold = soldSet.has(seatId);
                                        const isHeld = heldSet.has(seatId);
                                        const isUnavailable = isSold || isHeld;
                                        const isDisabled = (canPick && !isSelected && selectedCount >= maxSelectable) || isUnavailable;
                                        const sx = b.x + (s.x || 0);
                                        const sy = b.y + (s.y || 0);
                                        return (
                                            <g
                                                key={seatId}
                                                onClick={(e) => handleSeatClick(e, seatId, canPick, isDisabled, isUnavailable)}
                                                onMouseDown={(e) => { e.stopPropagation(); mouseDragRef.current.moved = false; }}
                                                style={{ cursor: canPick && !isDisabled ? "pointer" : "default" }}
                                            >
                                                <circle cx={sx} cy={sy} r={14} fill="transparent" />
                                                <circle
                                                    cx={sx} cy={sy} r={9}
                                                    fill={
                                                        isSelected ? "rgba(34,197,94,0.95)"
                                                        : isSold ? "rgba(239,68,68,0.90)"
                                                        : isHeld ? "rgba(245,158,11,0.90)"
                                                        : isDisabled ? "rgba(255,255,255,0.25)"
                                                        : "rgba(255,255,255,0.85)"
                                                    }
                                                    stroke="rgba(0,0,0,0.35)"
                                                    strokeWidth={1}
                                                />
                                                <text
                                                    x={sx} y={sy}
                                                    textAnchor="middle" dy="0.35em" fontSize="6.5"
                                                    fill="rgba(0,0,0,0.9)" fontFamily="ui-sans-serif, system-ui"
                                                    style={{ pointerEvents: "none", userSelect: "none" }}
                                                >
                                                    {s.label}
                                                </text>
                                            </g>
                                        );
                                    })}

                                </g>
                            );
                        })}
                    </g>
                </svg>
            </div>

            <div className="flex flex-wrap justify-center gap-x-5 gap-y-2 mt-3">
                <div className="flex items-center gap-1.5">
                    <div className="h-4 w-4 rounded-full bg-[rgba(255,255,255,0.85)] border border-white/30" />
                    <span className="text-sm text-white/80">Libre</span>
                </div>
                <div className="flex items-center gap-1.5">
                    <div className="h-4 w-4 rounded-full bg-[rgba(34,197,94,0.95)]" />
                    <span className="text-sm text-white/80">Seleccionado</span>
                </div>
                <div className="flex items-center gap-1.5">
                    <div className="h-4 w-4 rounded-full bg-[rgba(245,158,11,0.90)]" />
                    <span className="text-sm text-white/80">Reservado</span>
                </div>
                <div className="flex items-center gap-1.5">
                    <div className="h-4 w-4 rounded-full bg-[rgba(239,68,68,0.90)]" />
                    <span className="text-sm text-white/80">No disponible</span>
                </div>
            </div>
        </div>
    );
}
